import {
  Box,
  Button,
  ButtonGroup,
  Flex,
  HStack,
  Heading,
  Input,
  NativeSelectField,
  NativeSelectRoot,
  Table,
  Text,
} from "@chakra-ui/react"
import { useMemo, useState } from "react"
import { LuArrowDown, LuArrowUp, LuArrowUpDown } from "react-icons/lu"

export interface PerformanceRankingItem {
  key: string
  name: string
  correctnessRate: number
  avgTimeSeconds?: number
  totalQuestions?: number
}

export type RankingBand = "low" | "mid" | "high"
export type RankingSortBy = "correctness" | "time" | "name"
export type RankingSortDir = "asc" | "desc"

export const BAND_LOW_MAX = 40
export const BAND_HIGH_MIN = 70

export function classifyBand(correctnessRate: number): RankingBand {
  if (correctnessRate < BAND_LOW_MAX) {
    return "low"
  }
  if (correctnessRate <= BAND_HIGH_MIN) {
    return "mid"
  }
  return "high"
}

export const BAND_STYLES: Record<
  RankingBand,
  { fill: string; label: string; legend: string }
> = {
  low: { fill: "red.500", label: "red.600", legend: "< 40% needs review" },
  mid: { fill: "orange.400", label: "orange.600", legend: "40–70%" },
  high: { fill: "green.500", label: "green.600", legend: "> 70% strong" },
}

export function filterRankingItems(
  items: PerformanceRankingItem[],
  query: string,
): PerformanceRankingItem[] {
  const trimmed = query.trim().toLowerCase()
  if (!trimmed) {
    return items
  }
  return items.filter((item) => item.name.toLowerCase().includes(trimmed))
}

export function sortRankingItems(
  items: PerformanceRankingItem[],
  sortBy: RankingSortBy,
  sortDir: RankingSortDir,
): PerformanceRankingItem[] {
  const dir = sortDir === "asc" ? 1 : -1
  return [...items].sort((left, right) => {
    if (sortBy === "name") {
      return left.name.localeCompare(right.name) * dir
    }
    if (sortBy === "time") {
      const leftTime = left.avgTimeSeconds
      const rightTime = right.avgTimeSeconds
      if (leftTime == null && rightTime == null) {
        return 0
      }
      if (leftTime == null) {
        return 1
      }
      if (rightTime == null) {
        return -1
      }
      return (leftTime - rightTime) * dir
    }
    return (left.correctnessRate - right.correctnessRate) * dir
  })
}

interface PerformanceRankingProps {
  title: string
  items: PerformanceRankingItem[]
  itemNoun: string
  /**
   * When true (default), bars and correctness labels are colored by
   * performance band and a band legend is shown. When false, all bars use a
   * single neutral color, labels use default text color, and no legend renders.
   */
  showBands?: boolean
}

const CORRECTNESS_MAX = 100
const NEUTRAL_FILL = "gray.400"

export function PerformanceRanking({
  title,
  items,
  itemNoun,
  showBands = true,
}: PerformanceRankingProps) {
  const [sortBy, setSortBy] = useState<RankingSortBy>("correctness")
  const [sortDir, setSortDir] = useState<RankingSortDir>("asc")
  const [filter, setFilter] = useState("")
  const [view, setView] = useState<"bars" | "table">("bars")

  const hasTime = useMemo(
    () => items.some((item) => item.avgTimeSeconds != null),
    [items],
  )

  const hasQuestions = useMemo(
    () => items.some((item) => item.totalQuestions != null),
    [items],
  )

  const visibleItems = useMemo(
    () => sortRankingItems(filterRankingItems(items, filter), sortBy, sortDir),
    [items, filter, sortBy, sortDir],
  )

  if (items.length === 0) {
    return (
      <Box borderWidth="1px" borderRadius="lg" p={6} bg="bg.surface">
        <Heading size="lg" mb={4}>
          {title}
        </Heading>
        <Flex align="center" justify="center" minH="180px">
          <Text color="fg.muted">No {itemNoun} performance data yet.</Text>
        </Flex>
      </Box>
    )
  }

  const ariaSort = (column: RankingSortBy) =>
    sortBy === column
      ? sortDir === "asc"
        ? "ascending"
        : "descending"
      : "none"

  const handleHeaderSort = (column: RankingSortBy) => {
    if (column === sortBy) {
      setSortDir((dir) => (dir === "asc" ? "desc" : "asc"))
    } else {
      setSortBy(column)
      setSortDir(column === "name" ? "asc" : "desc")
    }
  }

  const sortIcon = (column: RankingSortBy) => {
    if (sortBy !== column) {
      return <LuArrowUpDown size={12} aria-hidden="true" />
    }
    return sortDir === "asc" ? (
      <LuArrowUp size={12} aria-hidden="true" />
    ) : (
      <LuArrowDown size={12} aria-hidden="true" />
    )
  }

  return (
    <Box borderWidth="1px" borderRadius="lg" p={6} bg="bg.surface">
      <Flex
        justify="space-between"
        align={{ base: "stretch", md: "center" }}
        direction={{ base: "column", md: "row" }}
        gap={3}
        mb={4}
      >
        <Heading size="lg">{title}</Heading>
        <HStack gap={2} wrap="wrap">
          <NativeSelectRoot size="sm" w="auto">
            <NativeSelectField
              aria-label={`Sort ${itemNoun}s`}
              value={`${sortBy}:${sortDir}`}
              onChange={(event) => {
                const [nextBy, nextDir] = event.target.value.split(":") as [
                  RankingSortBy,
                  RankingSortDir,
                ]
                setSortBy(nextBy)
                setSortDir(nextDir)
              }}
            >
              <option value="correctness:asc">Correctness (low → high)</option>
              <option value="correctness:desc">Correctness (high → low)</option>
              {hasTime && (
                <option value="time:desc">Avg time (slowest first)</option>
              )}
              {hasTime && (
                <option value="time:asc">Avg time (fastest first)</option>
              )}
              <option value="name:asc">Name (A → Z)</option>
              <option value="name:desc">Name (Z → A)</option>
            </NativeSelectField>
          </NativeSelectRoot>
          <Input
            size="sm"
            w={{ base: "full", md: "180px" }}
            placeholder={`Filter ${itemNoun}s…`}
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
          />
          <Text fontSize="xs" color="fg.muted" whiteSpace="nowrap">
            {visibleItems.length} {itemNoun}
            {visibleItems.length === 1 ? "" : "s"}
          </Text>
          <ButtonGroup size="sm" attached variant="outline">
            <Button
              aria-label="Bars view"
              aria-pressed={view === "bars"}
              variant={view === "bars" ? "solid" : "outline"}
              onClick={() => setView("bars")}
            >
              Bars
            </Button>
            <Button
              aria-label="Table view"
              aria-pressed={view === "table"}
              variant={view === "table" ? "solid" : "outline"}
              onClick={() => setView("table")}
            >
              Table
            </Button>
          </ButtonGroup>
        </HStack>
      </Flex>

      {view === "bars" ? (
        <>
          <Box maxH="420px" overflowY="auto">
            <Flex direction="column" gap={2}>
              {visibleItems.map((item) => {
                const band = classifyBand(item.correctnessRate)
                const style = BAND_STYLES[band]
                return (
                  <Flex
                    key={item.key}
                    data-testid="ranking-row"
                    align="center"
                    gap={3}
                    aria-label={`${item.name}: ${item.correctnessRate.toFixed(
                      0,
                    )}% correct${
                      item.avgTimeSeconds != null
                        ? `, ${item.avgTimeSeconds.toFixed(0)}s average`
                        : ""
                    }`}
                  >
                    <Text
                      flex="0 0 160px"
                      fontSize="sm"
                      textAlign="right"
                      truncate
                      title={item.name}
                    >
                      {item.name}
                    </Text>
                    <Box
                      flex="1"
                      bg="bg.muted"
                      borderRadius="sm"
                      h="18px"
                      overflow="hidden"
                    >
                      <Box
                        h="100%"
                        bg={showBands ? style.fill : NEUTRAL_FILL}
                        borderRadius="sm"
                        w={`${Math.min(
                          (item.correctnessRate / CORRECTNESS_MAX) * 100,
                          100,
                        )}%`}
                      />
                    </Box>
                    <Text
                      flex="0 0 40px"
                      fontSize="sm"
                      fontWeight="medium"
                      color={showBands ? style.label : undefined}
                      textAlign="right"
                    >
                      {item.correctnessRate.toFixed(0)}%
                    </Text>
                    {item.avgTimeSeconds != null && (
                      <Text
                        flex="0 0 56px"
                        fontSize="xs"
                        color="fg.muted"
                        textAlign="right"
                      >
                        {item.avgTimeSeconds.toFixed(0)}s avg
                      </Text>
                    )}
                  </Flex>
                )
              })}
            </Flex>
          </Box>

          {showBands && (
            <HStack gap={4} mt={4} fontSize="xs" color="fg.muted" wrap="wrap">
              {(["low", "mid", "high"] as const).map((band) => (
                <HStack key={band} gap={1}>
                  <Box
                    w="10px"
                    h="10px"
                    borderRadius="xs"
                    bg={BAND_STYLES[band].fill}
                  />
                  <Text>{BAND_STYLES[band].legend}</Text>
                </HStack>
              ))}
            </HStack>
          )}
        </>
      ) : (
        <Box maxH="420px" overflowY="auto">
          <Table.Root variant="outline" size="sm" stickyHeader>
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader aria-sort={ariaSort("name")}>
                  <Button
                    variant="ghost"
                    size="xs"
                    px={0}
                    color="inherit"
                    onClick={() => handleHeaderSort("name")}
                  >
                    <Flex align="center" gap={1}>
                      <Text as="span" fontWeight="semibold">
                        {itemNoun.charAt(0).toUpperCase() + itemNoun.slice(1)}
                      </Text>
                      {sortIcon("name")}
                    </Flex>
                  </Button>
                </Table.ColumnHeader>
                <Table.ColumnHeader
                  textAlign="right"
                  aria-sort={ariaSort("correctness")}
                >
                  <Button
                    variant="ghost"
                    size="xs"
                    px={0}
                    color="inherit"
                    onClick={() => handleHeaderSort("correctness")}
                  >
                    <Flex align="center" gap={1} justify="flex-end">
                      <Text as="span" fontWeight="semibold">
                        Correctness %
                      </Text>
                      {sortIcon("correctness")}
                    </Flex>
                  </Button>
                </Table.ColumnHeader>
                {hasQuestions && (
                  <Table.ColumnHeader textAlign="right">
                    Questions
                  </Table.ColumnHeader>
                )}
                {hasTime && (
                  <Table.ColumnHeader
                    textAlign="right"
                    aria-sort={ariaSort("time")}
                  >
                    <Button
                      variant="ghost"
                      size="xs"
                      px={0}
                      color="inherit"
                      onClick={() => handleHeaderSort("time")}
                    >
                      <Flex align="center" gap={1} justify="flex-end">
                        <Text as="span" fontWeight="semibold">
                          Avg time
                        </Text>
                        {sortIcon("time")}
                      </Flex>
                    </Button>
                  </Table.ColumnHeader>
                )}
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {visibleItems.map((item) => {
                const style = BAND_STYLES[classifyBand(item.correctnessRate)]
                return (
                  <Table.Row key={item.key}>
                    <Table.Cell>{item.name}</Table.Cell>
                    <Table.Cell textAlign="right">
                      <Flex align="center" gap={2} justify="flex-end">
                        <Box
                          w="60px"
                          h="6px"
                          bg="bg.muted"
                          borderRadius="full"
                          overflow="hidden"
                        >
                          <Box
                            h="100%"
                            bg={showBands ? style.fill : NEUTRAL_FILL}
                            w={`${Math.min(item.correctnessRate, 100)}%`}
                          />
                        </Box>
                        <Text
                          fontWeight="medium"
                          color={showBands ? style.label : undefined}
                        >
                          {item.correctnessRate.toFixed(0)}%
                        </Text>
                      </Flex>
                    </Table.Cell>
                    {hasQuestions && (
                      <Table.Cell textAlign="right">
                        {item.totalQuestions ?? "—"}
                      </Table.Cell>
                    )}
                    {hasTime && (
                      <Table.Cell textAlign="right" color="fg.muted">
                        {item.avgTimeSeconds != null
                          ? `${item.avgTimeSeconds.toFixed(0)}s`
                          : "—"}
                      </Table.Cell>
                    )}
                  </Table.Row>
                )
              })}
            </Table.Body>
          </Table.Root>
        </Box>
      )}
    </Box>
  )
}
